import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { formatNum, apiErr } from "../lib/utils";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "./ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ResponsiveContainer, AreaChart, Area, Tooltip as RTooltip } from "recharts";
import { Download, Trash2, Filter, Leaf, Users, ShieldCheck, Pencil, Check, X, Tags, Sparkles, CalendarClock, FileSearch, ClipboardCheck, GitCompare, Building2, ArrowLeftRight, Calculator, ScrollText, AlertTriangle, History, ArrowRight, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import { AiImportDialog } from "./AiImportDialog";
import { AUDIT_ACTION_LABEL, ProvenanceDialog } from "./ProvenanceDialog";
import { ExportReconciliation } from "./ExportReconciliation";
import { useAuth } from "../contexts/AuthContext";

const PILLAR_TONE = { Environment: "success", Social: "default", Governance: "warning" };
const PILLAR_ICON = { Environment: Leaf, Social: Users, Governance: ShieldCheck };
const RANGES = [
  { v: "all", l: "All time" }, { v: "this_month", l: "This month" },
  { v: "this_quarter", l: "This quarter" }, { v: "this_year", l: "This year" }, { v: "year", l: "Specific year" },
];

const ORIGIN_LABEL = { measured: "Measured", calculated: "Calculated", estimate: "Estimate", declaration: "Declaration", ai_inferred: "AI-inferred" };
const ORIGIN_TONE = { measured: "success", calculated: "success", estimate: "warning", declaration: "outline", ai_inferred: "warning" };

function ReportingCell({ it }) {
  const r = it.reporting;
  if (!r || r.value == null) return <span className="text-muted-foreground">—</span>;
  if (r.kind === "currency" && r.fx_status === "pending") {
    return <Badge variant="warning" className="text-[9px]" data-testid="reporting-pending"
      title={`Original ${formatNum(r.original_value)} ${r.original_unit} · awaiting FX ${r.original_unit}→${r.unit}`}>Rate pending</Badge>;
  }
  const title = r.kind === "currency"
    ? (r.converted ? `Original ${formatNum(r.original_value)} ${r.original_unit} · rate ${r.fx_rate} on ${r.fx_rate_date}` : "Same currency")
    : (r.converted ? `Original ${formatNum(r.original_value)} ${r.original_unit}` : "Reporting unit = original unit");
  return (
    <span title={title}>
      {formatNum(r.value)} {r.unit}
      {r.converted && <span className="block text-[9px] text-muted-foreground" data-testid="reporting-original">from {formatNum(r.original_value)} {r.original_unit}</span>}
    </span>
  );
}


function TrendCard({ title, color, icon: Icon, series, value, unit }) {
  const empty = !series.some((s) => s.v > 0);
  return (
    <div className="card-flat p-3" data-testid={`trend-${title.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="inline-flex items-center gap-1.5 text-xs"><Icon className="h-3.5 w-3.5" style={{ color }} /> {title}</div>
        <span className="font-mono text-sm font-semibold" style={{ color }}>{value}<span className="text-[10px] text-muted-foreground ml-0.5">{unit}</span></span>
      </div>
      <div className="h-10">
        {empty ? <div className="h-full flex items-center text-[10px] text-muted-foreground">No trend data</div> : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
              <defs><linearGradient id={`g-${title}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.5} /><stop offset="100%" stopColor={color} stopOpacity={0} /></linearGradient></defs>
              <RTooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 11 }} labelStyle={{ color: "#999" }} formatter={(v) => [formatNum(v, { decimals: 2 }), title]} />
              <Area type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} fill={`url(#g-${title})`} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export function EsgRecordsBrowser({ reloadKey }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canVerify = ["tenant_admin", "esg_manager", "super_admin"].includes(user?.role);
  const [data, setData] = useState(null);
  const [pillar, setPillar] = useState(() => searchParams.get("pillar") || "all");
  const [category, setCategory] = useState(() => searchParams.get("category") || "all");
  const [range, setRange] = useState(() => searchParams.get("range") || "year");
  const [year, setYear] = useState("2024");
  const [activity, setActivity] = useState(() => searchParams.get("activity") || "");
  const [selected, setSelected] = useState({});
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [recatOpen, setRecatOpen] = useState(false);
  const [taxonomy, setTaxonomy] = useState({});
  const [recat, setRecat] = useState({ catId: "", activity: "" });
  const [importOpen, setImportOpen] = useState(false);
  const [reconOpen, setReconOpen] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignFac, setAssignFac] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [replaceInfo, setReplaceInfo] = useState(null); // Replace History — old-vs-new dialog
  const [unverifiedOnly, setUnverifiedOnly] = useState(false);
  const [origin, setOrigin] = useState(() => searchParams.get("origin") || "all");
  const [provenance, setProvenance] = useState("all");
  const [swapItem, setSwapItem] = useState(null);
  const [swapCur, setSwapCur] = useState(null);
  const [swapCands, setSwapCands] = useState([]);
  const [swapForm, setSwapForm] = useState({ factor_code: "", value: "", unit: "" });
  const [swapQ, setSwapQ] = useState("");
  const [swapBusy, setSwapBusy] = useState(false);
  const [notCalcOnly, setNotCalcOnly] = useState(false);
  const [assignItem, setAssignItem] = useState(null);
  const [assignCands, setAssignCands] = useState([]);
  const [assignQ, setAssignQ] = useState("");
  const [assignFactorCode, setAssignFactorCode] = useState("");
  const [assignBusy, setAssignBusy] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [logEvents, setLogEvents] = useState([]);
  const [fxEscalatedOnly, setFxEscalatedOnly] = useState(false);
  const [changeLogOpen, setChangeLogOpen] = useState(false);
  const [changeLog, setChangeLog] = useState({ entries: [], total: 0, page: 1, pages: 1 });
  const [provItem, setProvItem] = useState(null);
  const [facilitySel, setFacilitySel] = useState([]);   // RD-30 — multi-select facility filter
  const [facMenuOpen, setFacMenuOpen] = useState(false);
  const [groupSel, setGroupSel] = useState("all");   // Group Taxonomy — single-select group filter

  const query = useCallback(() => {
    const params = {};
    if (pillar !== "all") params.pillar = pillar;
    if (category !== "all") params.category = category;
    if (range === "year") params.year = year || new Date().getFullYear();
    else if (range !== "all") params.range = range;
    if (showArchived) params.archived = true;
    if (origin !== "all") params.origin = origin;
    if (groupSel !== "all") params.group = groupSel;   // Group Taxonomy — scopes list + export
    if (facilitySel.length) params.facility = facilitySel.join(",");   // RD-30 — scopes list + export
    return params;
  }, [pillar, category, range, year, showArchived, origin, facilitySel, groupSel]);

  const load = useCallback(() => {
    api.get("/esg-records/list", { params: query() })
      .then((r) => { setData(r.data); setSelected({}); }).catch((e) => toast.error(apiErr(e)));
  }, [query]);
  useEffect(() => { load(); }, [load, reloadKey]);
  useEffect(() => { api.get("/esg-taxonomy").then((r) => setTaxonomy(r.data.taxonomy)).catch(() => {}); }, []);
  useEffect(() => { api.get("/facilities").then((r) => setFacilities(r.data.facilities || [])).catch(() => {}); }, []);

  const catOptions = useMemo(() => {
    if (!data) return [];
    if (pillar === "all") return [...new Set(Object.values(data.categories || {}).flat())].sort();
    return data.categories?.[pillar] || [];
  }, [data, pillar]);

  // RD-30 — facility universe from the backend facet (unaffected by the facility filter), plus
  // any currently-selected names so a selection never disappears from the menu.
  const facilityFilterOptions = useMemo(() => {
    const s = new Set(data?.facilities || []);
    facilitySel.forEach((f) => s.add(f));
    return [...s].sort();
  }, [data, facilitySel]);
  const toggleFacility = (name) =>
    setFacilitySel((p) => (p.includes(name) ? p.filter((x) => x !== name) : [...p, name]));

  // Trend series grouped by period (ascending)
  const trends = useMemo(() => {
    const periods = [...new Set((data?.items || []).map((i) => i.period).filter(Boolean))].sort();
    const mk = (fn) => periods.map((p) => ({ period: p, v: fn(p) }));
    const sum = (p, pil, field) => (data.items || []).filter((i) => i.period === p && i.pillar === pil).reduce((a, i) => a + (field ? (i[field] || 0) : 1), 0);
    return {
      env: mk((p) => sum(p, "Environment", "co2e_tonnes")),
      soc: mk((p) => sum(p, "Social")),
      gov: mk((p) => sum(p, "Governance")),
    };
  }, [data]);

  const onPillar = (p) => { setPillar(p); setCategory("all"); };

  const visibleItems = useMemo(() => {
    let items = data?.items || [];
    if (activity) items = items.filter((i) => i.activity_type === activity);
    if (provenance === "actual") items = items.filter((i) => !i.synthetic && !i.originally_synthetic);
    else if (provenance === "originally_synthetic") items = items.filter((i) => i.originally_synthetic && !i.synthetic);
    else if (provenance === "synthetic") items = items.filter((i) => i.synthetic);
    if (unverifiedOnly) items = items.filter((i) => i.status === "unverified");
    if (notCalcOnly) items = items.filter((i) => i.not_calculated);
    if (fxEscalatedOnly) items = items.filter((i) => i.fx_escalated);
    return items;
  }, [data, activity, provenance, unverifiedOnly, notCalcOnly, fxEscalatedOnly]);
  const clearActivity = () => {
    setActivity("");
    const sp = new URLSearchParams(searchParams);
    ["activity", "pillar", "category", "range"].forEach((k) => sp.delete(k));
    setSearchParams(sp, { replace: true });
    setPillar("all"); setCategory("all"); setRange("year");
  };

  const selectedRefs = useMemo(() => Object.keys(selected).filter((k) => selected[k]).map((k) => { const [source, ...rest] = k.split("|"); return { source, id: rest.join("|") }; }), [selected]);
  const allKeys = useMemo(() => visibleItems.map((i) => `${i.source}|${i.id}`), [visibleItems]);
  const allSelected = allKeys.length > 0 && allKeys.every((k) => selected[k]);
  const toggleAll = () => { if (allSelected) setSelected({}); else setSelected(Object.fromEntries(allKeys.map((k) => [k, true]))); };
  const toggle = (k) => setSelected((p) => ({ ...p, [k]: !p[k] }));

  const startEdit = (it) => { setEditing(`${it.source}|${it.id}`); setEditForm({ value: it.value ?? "", period: it.period ?? "", notes: it.notes ?? "", facility: it.facility ?? "", origin: it.origin ?? "", read_type: it.read_type ?? "" }); };
  const saveEdit = async (it) => {
    try {
      await api.patch("/esg-records/item", { id: it.id, source: it.source, value: editForm.value === "" ? null : Number(editForm.value), period: editForm.period, notes: editForm.notes, facility: editForm.facility ?? "", origin: editForm.origin || undefined, read_type: editForm.read_type || undefined });
      toast.success("Record updated"); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const remove = async (it) => {
    if (!window.confirm("Delete this record?")) return;
    const url = it.source === "data_points" ? `/data/${it.id}` : `/esg-records/${it.id}`;
    try { await api.delete(url); toast.success("Record deleted"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedRefs.length} selected record(s)?`)) return;
    try { const r = await api.post("/esg-records/bulk-delete", { items: selectedRefs }); toast.success(`Deleted ${r.data.deleted} record(s)`); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const bulkVerify = async (verified) => {
    try {
      const r = await api.post("/esg-records/bulk-verify", { items: selectedRefs, verified });
      toast.success(`${verified ? "Verified" : "Marked Unverified"} ${r.data.updated} record(s)`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const allCats = useMemo(() => Object.values(taxonomy).flat(), [taxonomy]);
  const recatCat = allCats.find((c) => c.id === recat.catId);
  const applyRecat = async () => {
    if (!recatCat) { toast.error("Pick a category"); return; }
    try {
      const r = await api.post("/esg-records/bulk-update", { items: selectedRefs, category: recatCat.category, activity_type: recat.activity || undefined });
      toast.success(`Re-categorised ${r.data.updated} record(s)`); setRecatOpen(false); setRecat({ catId: "", activity: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const applyAssignFacility = async () => {
    try {
      const r = await api.post("/esg-records/bulk-update", { items: selectedRefs, facility: assignFac === "__none__" ? "" : assignFac });
      toast.success(`Facility updated on ${r.data.updated} record(s)`); setAssignOpen(false); setAssignFac(""); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const restore = async (it) => {
    try {
      await api.post("/esg-records/restore", { items: [{ id: it.id, source: it.source }] });
      toast.success("Record restored"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openSwap = async (it) => {
    setSwapItem(it); setSwapCur(null); setSwapCands([]); setSwapQ("");
    setSwapForm({ factor_code: "", value: "", unit: "" });
    try {
      const r = await api.get(`/data/${it.id}/swap-candidates`);
      setSwapCur(r.data.current); setSwapCands(r.data.candidates);
    } catch (e) { toast.error(apiErr(e)); setSwapItem(null); }
  };
  const searchSwap = async (q) => {
    setSwapQ(q);
    try { const r = await api.get(`/data/${swapItem.id}/swap-candidates`, { params: q ? { q } : {} }); setSwapCands(r.data.candidates); } catch (e) { /* keep list */ }
  };
  const chooseFactor = (f) => setSwapForm((s) => ({ ...s, factor_code: f.code, unit: s.unit || f.unit || "" }));
  const applySwap = async () => {
    if (!swapForm.factor_code) { toast.error("Pick a primary factor to upgrade to"); return; }
    if (!swapForm.value || Number(swapForm.value) <= 0) { toast.error("Enter the physical quantity"); return; }
    if (!swapForm.unit) { toast.error("Enter the unit"); return; }
    setSwapBusy(true);
    try {
      const r = await api.post(`/data/${swapItem.id}/swap-factor`, { factor_code: swapForm.factor_code, value: Number(swapForm.value), unit: swapForm.unit });
      toast.success(`Upgraded to primary factor — ${formatNum(r.data.co2e_tonnes, { decimals: 3 })} tCO₂e (was ${formatNum(r.data.prior_co2e, { decimals: 3 })})`);
      setSwapItem(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSwapBusy(false); }
  };

  const openAssign = async (it) => {
    setAssignItem(it); setAssignFactorCode(""); setAssignQ("");
    try {
      const r = await api.get("/emissions/factors", { params: it.is_monetary ? { basis: "spend" } : {} });
      setAssignCands((r.data.factors || []).slice(0, 80));
    } catch (e) { setAssignCands([]); }
  };
  const searchAssign = async (q) => {
    setAssignQ(q);
    try {
      const r = await api.get("/emissions/factors", { params: { ...(q ? { q } : {}), ...(assignItem?.is_monetary ? { basis: "spend" } : {}) } });
      setAssignCands((r.data.factors || []).slice(0, 80));
    } catch (e) { /* keep list */ }
  };
  const applyAssign = async () => {
    if (!assignFactorCode) { toast.error("Pick an emission factor"); return; }
    setAssignBusy(true);
    try {
      if (assignItem?.bulk) {
        const r = await api.post("/esg-records/assign-factor-bulk", { items: selectedRefs, factor_code: assignFactorCode });
        toast.success(`Assigned factor to ${r.data.updated} record(s)${r.data.skipped ? ` · ${r.data.skipped} skipped` : ""}`);
      } else {
        const r = await api.post("/esg-records/assign-factor", { id: assignItem.id, source: assignItem.source, factor_code: assignFactorCode });
        toast.success(r.data.not_calculated ? "Factor assigned — still uncalculated (no converted amount yet)" : `Calculated: ${formatNum(r.data.co2e_tonnes, { decimals: 3 })} tCO₂e`);
      }
      setAssignItem(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setAssignBusy(false); }
  };
  const openLog = async () => {
    setLogOpen(true);
    try { const r = await api.get("/esg-records/verification-log", { params: { limit: 100 } }); setLogEvents(r.data.events || []); } catch (e) { setLogEvents([]); }
  };
  const loadChangeLog = async (page) => {
    try {
      const r = await api.get("/esg-records/audit-log", { params: { page, page_size: 25 } });
      setChangeLog({ entries: r.data.entries || [], total: r.data.total, page: r.data.page, pages: r.data.pages });
    } catch (e) { toast.error(apiErr(e)); }
  };
  const openChangeLog = () => { setChangeLogOpen(true); loadChangeLog(1); };
  const downloadChangeHistoryCsv = async () => {
    try {
      const r = await api.get("/esg-records/audit-log/export.csv", { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = `record_change_history_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.csv`; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const downloadAuditCsv = async () => {
    try {
      const r = await api.get("/esg-records/verification-log.csv", { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = `verification_audit_log_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.csv`; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const openBulkAssign = async () => {
    setAssignItem({ bulk: true, count: selectedRefs.length }); setAssignFactorCode(""); setAssignQ("");
    try { const r = await api.get("/emissions/factors"); setAssignCands((r.data.factors || []).slice(0, 80)); } catch (e) { setAssignCands([]); }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/esg-records/export.csv", { params: query(), responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = "esg_records.csv"; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!data) return <div className="p-6 text-muted-foreground font-mono text-sm">Loading records…</div>;

  return (
    <div className="space-y-4" data-testid="esg-records-browser">
      {/* Trend mini-charts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <TrendCard title="Environment" color="#00E65B" icon={Leaf} series={trends.env} value={formatNum(data.total_co2e, { decimals: 1 })} unit="tCO₂e" />
        <TrendCard title="Social" color="#60A5FA" icon={Users} series={trends.soc} value={data.by_pillar?.Social || 0} unit="records" />
        <TrendCard title="Governance" color="#FFB800" icon={ShieldCheck} series={trends.gov} value={data.by_pillar?.Governance || 0} unit="records" />
      </div>

      {/* Filters */}
      <div className="card-flat p-3 flex flex-wrap items-end gap-3">
        <div className="inline-flex items-center gap-1.5 text-muted-foreground text-xs uppercase tracking-[0.12em] mr-1"><Filter className="h-3.5 w-3.5" /> Filters</div>
        <div><div className="label-eyebrow text-[10px] mb-1">Pillar</div>
          <Select value={pillar} onValueChange={onPillar}>
            <SelectTrigger className="w-40" data-testid="filter-pillar"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All pillars</SelectItem>
              <SelectItem value="Environment">Environment</SelectItem>
              <SelectItem value="Social">Social</SelectItem>
              <SelectItem value="Governance">Governance</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><div className="label-eyebrow text-[10px] mb-1">Subcategory{pillar !== "all" && taxonomy?.[pillar]?.length ? <span data-testid="category-usage-count"> · {catOptions.length} of {taxonomy[pillar].length} in use</span> : null}</div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-52" data-testid="filter-category"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="all">All categories</SelectItem>
              {catOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><div className="label-eyebrow text-[10px] mb-1">Date range</div>
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-40" data-testid="filter-range"><SelectValue /></SelectTrigger>
            <SelectContent>{RANGES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {range === "year" && (
          <div><div className="label-eyebrow text-[10px] mb-1">Year</div>
            <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="w-28" placeholder="2024" data-testid="filter-year" /></div>
        )}
        <div><div className="label-eyebrow text-[10px] mb-1">Evidence origin</div>
          <Select value={origin} onValueChange={setOrigin}>
            <SelectTrigger className="w-44" data-testid="filter-origin"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All origins</SelectItem>
              {(data.origins || ["measured", "calculated", "estimate", "declaration", "ai_inferred"]).map((o) => (
                <SelectItem key={o} value={o}>{ORIGIN_LABEL[o] || o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div><div className="label-eyebrow text-[10px] mb-1">Provenance</div>
          <div className="inline-flex rounded-sm border border-border overflow-hidden" data-testid="filter-provenance">
            {[["all", "All"], ["actual", "Actual"], ["originally_synthetic", "Orig. synthetic"], ["synthetic", "Synthetic"]].map(([v, l]) => (
              <button key={v} onClick={() => setProvenance(v)} data-testid={`prov-filter-${v}`}
                className={`px-2.5 py-2 text-[11px] font-mono transition-colors ${provenance === v
                  ? "bg-accent text-accent-foreground font-bold"
                  : "text-muted-foreground hover:text-foreground hover:bg-surface-hover"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        {/* Group Taxonomy — group filter (placed before Facility) */}
        <div><div className="label-eyebrow text-[10px] mb-1">Group</div>
          <Select value={groupSel} onValueChange={setGroupSel}>
            <SelectTrigger className="w-48" data-testid="filter-group"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="all">All groups</SelectItem>
              <SelectItem value="__ungrouped__">Ungrouped</SelectItem>
              {(data.groups || []).map((g) => <SelectItem key={g.id} value={g.id}>{g.label || g.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {/* RD-30 — multi-select facility filter (scopes the table + CSV export) */}
        <div className="relative">
          <div className="label-eyebrow text-[10px] mb-1">Facility</div>
          <button type="button" onClick={() => setFacMenuOpen((o) => !o)} data-testid="filter-facility"
            className="h-10 min-w-[11rem] px-3 rounded-sm border border-border bg-background text-xs inline-flex items-center justify-between gap-2 hover:bg-surface-hover">
            <span className="truncate">{facilitySel.length ? `${facilitySel.length} selected` : "All facilities"}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 opacity-60 shrink-0" />
          </button>
          {facMenuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setFacMenuOpen(false)} data-testid="facility-filter-overlay" />
              <div className="absolute z-50 mt-1 w-60 max-h-72 overflow-y-auto rounded-sm border border-border bg-background shadow-lg p-1" data-testid="facility-filter-menu">
                {facilityFilterOptions.length === 0 && <div className="px-2 py-2 text-[11px] text-muted-foreground">No facilities</div>}
                {facilityFilterOptions.map((name) => {
                  const on = facilitySel.includes(name);
                  return (
                    <button key={name} type="button" onClick={() => toggleFacility(name)} data-testid={`facility-filter-opt-${name}`}
                      className="w-full text-left px-2 py-1.5 text-xs rounded-sm hover:bg-surface-hover flex items-center gap-2">
                      <span className={`h-3.5 w-3.5 border rounded-sm grid place-items-center shrink-0 ${on ? "bg-accent border-accent" : "border-border"}`}>{on && <Check className="h-2.5 w-2.5 text-accent-foreground" />}</span>
                      <span className="truncate">{name}</span>
                    </button>
                  );
                })}
                {facilitySel.length > 0 && (
                  <button type="button" onClick={() => setFacilitySel([])} data-testid="facility-filter-clear"
                    className="w-full text-left px-2 py-1.5 mt-1 border-t border-border text-[11px] text-muted-foreground hover:text-foreground">Clear selection</button>
                )}
              </div>
            </>
          )}
        </div>
        <div className="flex-1" />
        <Button variant="primary" onClick={() => setImportOpen(true)} data-testid="ai-import-btn"><Sparkles className="h-4 w-4" /> AI Import</Button>
        <Button variant="outline" onClick={() => setReconOpen(true)} data-testid="recon-report-btn"><GitCompare className="h-4 w-4" /> Reconciliation</Button>
        <Button variant={unverifiedOnly ? "primary" : "outline"} onClick={() => setUnverifiedOnly((v) => !v)} data-testid="unverified-only-toggle" title="Show only Unverified records — select them and use Verify to promote in bulk">
          <ShieldCheck className="h-4 w-4" /> {unverifiedOnly ? "Unverified only" : "Unverified"}
        </Button>
        <Button variant={notCalcOnly ? "primary" : "outline"} onClick={() => setNotCalcOnly((v) => !v)} data-testid="notcalc-only-toggle" title="Show only records imported but not calculated — assign a factor to calculate them">
          <Calculator className="h-4 w-4" /> {notCalcOnly ? "Not calculated only" : "Not calculated"}
        </Button>
        <Button variant={fxEscalatedOnly ? "primary" : "outline"} onClick={() => setFxEscalatedOnly((v) => !v)} data-testid="fx-escalated-toggle" title="Show only foreign-currency records escalated after sitting unconverted too long">
          <AlertTriangle className="h-4 w-4" /> {fxEscalatedOnly ? "FX escalated only" : "FX escalated"}
        </Button>
        <Button variant="outline" onClick={openLog} data-testid="verification-log-btn" title="Auditor log of who verified records and when">
          <ScrollText className="h-4 w-4" /> Audit log
        </Button>
        <Button variant="outline" onClick={openChangeLog} data-testid="change-history-btn" title="Full, immutable change history across every record — create, edit, reassignment, factor change, verification">
          <History className="h-4 w-4" /> Change history
        </Button>
        <Button variant={showArchived ? "primary" : "outline"} onClick={() => setShowArchived((v) => !v)} data-testid="archived-toggle-btn" title="Show soft-archived (superseded) records — restorable">
          <Trash2 className="h-4 w-4" /> {showArchived ? "Archived view" : "Archived"}
        </Button>
        <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn"><Download className="h-4 w-4" /> Export CSV</Button>
      </div>

      {/* Summary + bulk bar */}
      <div className="flex flex-wrap items-center gap-3">
        <Badge variant="outline" className="text-xs" data-testid="records-count">{(activity || provenance !== "all" || unverifiedOnly || notCalcOnly || fxEscalatedOnly) ? visibleItems.length : data.count} records</Badge>
        {data.reporting_locale && (
          <Badge variant="lime" className="text-xs" data-testid="reporting-locale-badge"
            title={`Reporting units — area ${data.reporting_locale.unit_labels?.area || data.reporting_locale.units?.area}, energy ${data.reporting_locale.unit_labels?.energy || data.reporting_locale.units?.energy}, volume ${data.reporting_locale.unit_labels?.volume || data.reporting_locale.units?.volume}`}>
            Reporting: {data.reporting_locale.currency}
          </Badge>
        )}
        {activity && (
          <Badge variant="warning" className="text-xs inline-flex items-center gap-1" data-testid="evidence-filter-chip">
            <FileSearch className="h-3 w-3" /> Evidence for: {activity}
            <button onClick={clearActivity} className="ml-1 hover:text-foreground" data-testid="clear-evidence-filter"><X className="h-3 w-3" /></button>
          </Badge>
        )}
        {facilitySel.length > 0 && (
          <Badge variant="lime" className="text-xs inline-flex items-center gap-1" data-testid="facility-filter-chip">
            <Building2 className="h-3 w-3" /> {facilitySel.length === 1 ? facilitySel[0] : `${facilitySel.length} facilities`} · export scoped
            <button onClick={() => setFacilitySel([])} className="ml-1 hover:text-foreground" data-testid="clear-facility-filter"><X className="h-3 w-3" /></button>
          </Badge>
        )}
        {Object.entries(data.by_pillar || {}).map(([p, n]) => {
          const Icon = PILLAR_ICON[p];
          return <Badge key={p} variant={PILLAR_TONE[p]} className="text-xs inline-flex items-center gap-1"><Icon className="h-3 w-3" /> {p}: {n}</Badge>;
        })}
        {selectedRefs.length > 0 && (
          <div className="ml-auto flex items-center gap-2" data-testid="bulk-action-bar">
            <span className="text-xs text-muted-foreground">{selectedRefs.length} selected</span>
            {canVerify && (
              <>
                <Button variant="outline" size="sm" className="text-[#00E65B] border-[#00E65B]/30" onClick={() => bulkVerify(true)} data-testid="bulk-verify-btn"><ShieldCheck className="h-3.5 w-3.5" /> Verify</Button>
                <Button variant="outline" size="sm" className="text-[#FFB800] border-[#FFB800]/30" onClick={() => bulkVerify(false)} data-testid="bulk-unverify-btn"><X className="h-3.5 w-3.5" /> Un-verify</Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={() => setAssignOpen(true)} data-testid="bulk-assign-facility-btn"><Building2 className="h-3.5 w-3.5" /> Assign facility</Button>
            <Button variant="outline" size="sm" onClick={openBulkAssign} data-testid="bulk-assign-factor-btn"><Calculator className="h-3.5 w-3.5" /> Assign factor</Button>
            <Button variant="outline" size="sm" onClick={() => setRecatOpen(true)} data-testid="bulk-recat-btn"><Tags className="h-3.5 w-3.5" /> Re-categorise</Button>
            <Button variant="outline" size="sm" className="text-red-400 border-red-500/30" onClick={bulkDelete} data-testid="bulk-delete-btn"><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card-flat overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-3.5 w-3.5 accent-[hsl(var(--accent))]" data-testid="select-all" /></TableHead>
              <TableHead>Period</TableHead><TableHead>Pillar</TableHead><TableHead>Category</TableHead>
              <TableHead>Activity</TableHead><TableHead>Facility</TableHead><TableHead className="text-right">Value</TableHead>
              <TableHead className="text-right">Reporting</TableHead>
              <TableHead className="text-right">tCO₂e</TableHead><TableHead>Basis</TableHead><TableHead>Notes</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleItems.length === 0 && (
              <TableRow><TableCell colSpan={13} className="text-center text-muted-foreground py-10">No records match these filters.</TableCell></TableRow>
            )}
            {visibleItems.map((it) => {
              const key = `${it.source}|${it.id}`;
              const isEditing = editing === key;
              return (
              <TableRow key={key} data-testid="esg-record-row">
                <TableCell><input type="checkbox" checked={!!selected[key]} onChange={() => toggle(key)} className="h-3.5 w-3.5 accent-[hsl(var(--accent))]" data-testid="row-select" /></TableCell>
                <TableCell className="font-mono text-xs">{isEditing ? <Input value={editForm.period} onChange={(e) => setEditForm((f) => ({ ...f, period: e.target.value }))} className="h-7 w-24 text-xs" data-testid="edit-period" /> : (
                  <span className="inline-flex items-center gap-1">{it.period}
                    {it.is_baseline && <Badge variant="warning" className="text-[9px] gap-0.5" data-testid="baseline-badge"><CalendarClock className="h-2.5 w-2.5" /> Baseline</Badge>}
                    {it.kpi_evidence && <Badge variant="success" className="text-[9px] gap-0.5" data-testid="kpi-evidence-badge" title="This record is evidence for a Scorecard KPI"><ClipboardCheck className="h-2.5 w-2.5" /> Scorecard KPI</Badge>}
                  </span>
                )}</TableCell>
                <TableCell><Badge variant={PILLAR_TONE[it.pillar]} className="text-[10px]">{it.pillar?.[0]}</Badge></TableCell>
                <TableCell className="text-xs">{it.category}</TableCell>
                <TableCell className="font-medium text-sm">
                  {it.activity_type}
                  {it.origin && <Badge variant={ORIGIN_TONE[it.origin] || "outline"} className="text-[8px] ml-1.5 align-middle" title={`Evidence origin: ${ORIGIN_LABEL[it.origin] || it.origin}`} data-testid={`record-origin-${it.id}`}>{ORIGIN_LABEL[it.origin] || it.origin}</Badge>}
                  {it.read_type && <Badge variant={it.read_type === "actual" ? "success" : "warning"} className="text-[8px] ml-1 align-middle" title={`Read type: ${it.read_type === "actual" ? "Actual" : "Estimated"}`} data-testid={`record-readtype-${it.id}`}>{it.read_type === "actual" ? "Actual" : "Estimated"}</Badge>}
                  {isEditing && (
                    <div className="flex gap-1 mt-1.5">
                      <Select value={editForm.read_type || "__none__"} onValueChange={(v) => setEditForm((f) => ({ ...f, read_type: v === "__none__" ? "" : v }))}>
                        <SelectTrigger className="h-7 w-28 text-[11px]" data-testid="edit-read-type"><SelectValue placeholder="Read type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Read type…</SelectItem>
                          <SelectItem value="actual">Actual</SelectItem>
                          <SelectItem value="estimated">Estimated</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={editForm.origin || "__none__"} onValueChange={(v) => setEditForm((f) => ({ ...f, origin: v === "__none__" ? "" : v }))}>
                        <SelectTrigger className="h-7 w-32 text-[11px]" data-testid="edit-origin"><SelectValue placeholder="Origin" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Origin…</SelectItem>
                          {(data.origins || ["measured", "calculated", "estimate", "declaration", "ai_inferred"]).map((o) => (
                            <SelectItem key={o} value={o}>{ORIGIN_LABEL[o] || o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-xs">
                  {isEditing ? (
                    <Select value={editForm.facility || "__none__"} onValueChange={(v) => setEditForm((f) => ({ ...f, facility: v === "__none__" ? "" : v }))}>
                      <SelectTrigger className="h-7 w-36 text-xs" data-testid="edit-facility"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Unassigned</SelectItem>
                        {facilities.map((f) => <SelectItem key={f.id} value={f.name}>{f.name}</SelectItem>)}
                        {editForm.facility && !facilities.some((f) => f.name === editForm.facility) && <SelectItem value={editForm.facility}>{editForm.facility}</SelectItem>}
                      </SelectContent>
                    </Select>
                  ) : (it.facility ? <span data-testid="record-facility">{it.facility}</span> : <span className="text-muted-foreground" data-testid="record-facility">Unassigned</span>)}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {isEditing && it.value_type !== "boolean" ? (
                    <Input type="number" step="any" value={editForm.value} onChange={(e) => setEditForm((f) => ({ ...f, value: e.target.value }))} className="h-7 w-24 text-xs text-right" data-testid="edit-value" />
                  ) : it.value_type === "boolean" ? (it.bool_value ? "Yes" : "No") : `${formatNum(it.value)} ${it.unit || ""}`}
                  {!isEditing && it.coverage_pct != null && <span className="text-muted-foreground"> · {it.coverage_pct}%</span>}
                </TableCell>
                <TableCell className="text-right font-mono text-xs" data-testid="record-reporting"><ReportingCell it={it} />{it.fx_escalated && <Badge variant="danger" className="ml-1 text-[8px] align-middle" data-testid={`record-fx-escalated-${it.id}`} title="FX escalated — awaiting a rate for too long; run the FX sweep or check the currency/date">FX escalated</Badge>}</TableCell>
                <TableCell className="text-right font-mono text-xs">{it.not_calculated ? <Badge variant="outline" className="text-[9px] border-slate-400/40 text-slate-300" data-testid={`record-notcalc-${it.id}`} title="Imported, not calculated — no emission factor applied yet">Not calculated</Badge> : it.co2e_tonnes != null ? formatNum(it.co2e_tonnes, { decimals: 2 }) : "—"}</TableCell>
                <TableCell className="text-xs">
                  {it.emission_basis === "spend_based" ? (
                    <Badge variant="warning" className="text-[9px] whitespace-nowrap" data-testid="record-basis-spend"
                      title="Spend-based EEIO estimate — lower quality. Upgrade to a primary factor when real data arrives.">
                      Spend-based · {it.data_quality_tier || "D"}
                    </Badge>
                  ) : it.upgraded_from_spend ? (
                    <Badge variant="success" className="text-[9px] whitespace-nowrap" data-testid="record-basis-upgraded"
                      title="Upgraded from a spend-based estimate to a primary/activity factor — prior estimate retained in lineage.">
                      Primary ✓
                    </Badge>
                  ) : it.provenance?.registry ? (
                    <Badge variant={it.provenance.registry === "SEFR" ? "success" : "outline"} className="text-[9px] whitespace-nowrap"
                      data-testid="record-provenance"
                      title={`${it.provenance.factor_name || it.provenance.factor_code}${it.provenance.source ? " · " + it.provenance.source : ""}${it.provenance.ipcc_version ? " · " + it.provenance.ipcc_version : ""}`}>
                      {it.provenance.registry === "SEFR" ? "SEFR·SG" : "IPCC"}{it.provenance.ipcc_version ? ` · ${it.provenance.ipcc_version}` : ""}
                    </Badge>
                  ) : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[160px]">
                  {isEditing ? <Input value={editForm.notes} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} className="h-7 text-xs" placeholder="Notes" data-testid="edit-notes" /> : (
                    <div className="truncate">
                      {it.notes || "—"}
                      {it.cost_status === "source_unavailable" && <span className="block text-[9px] text-[#FFB800]" data-testid={`record-cost-unavailable-${it.id}`} title="No source bill on file — bill cost is not shown rather than estimated">⚠ bill cost: source unavailable</span>}
                      {it.cost_status === "from_bill" && it.currency_amount != null && <span className="block text-[9px] text-muted-foreground" data-testid={`record-cost-${it.id}`}>cost {it.currency || ""} {formatNum(it.currency_amount)} · from bill</span>}
                      {it.supersedes_snapshot && it.supersedes_snapshot.length > 0 && <button className="block text-[9px] text-accent underline decoration-dotted mt-0.5" data-testid={`record-supersedes-${it.id}`} onClick={() => setReplaceInfo(it)} title="This record replaced a prior bill — click to compare old vs new">↺ replaced a prior bill</button>}
                      {it.source_doc && /^AI import:/.test(it.source_doc) && <span className="block text-[9px] text-accent" data-testid="origin-tag">⚡ {it.source_doc}</span>}
                      {it.kpi_entry && <span className="block text-[9px] text-sky-400" data-testid="kpi-entry-tag" title="Recorded via the Social & Governance 'Record value' entry — auditable trail">⌨ {it.source_doc || "KPI entry"}</span>}
                    </div>
                  )}
                </TableCell>
                <TableCell>{it.synthetic && <Badge variant="warning" className="mr-1 text-[9px]" title="Baseline-seeded example — purge or declare as actual in Data Management › Baseline Seeding">Synthetic</Badge>}{!it.synthetic && it.originally_synthetic && <Badge variant="outline" className="mr-1 text-[9px] border-[#FFB800]/40 text-[#FFB800]" data-testid={`record-originally-synthetic-${it.id}`} title="Promoted from a baseline-seeded example to actual — provenance permanently retained">Originally synthetic</Badge>}{it.superseded ? <Badge variant="warning" data-testid={it.superseded_by_record_id ? `record-replaced-${it.id}` : undefined} title={it.superseded_reason || "Soft-archived (superseded)"}>{it.superseded_by_record_id ? "Replaced" : "Archived"}</Badge> : it.status === "flagged" ? <Badge variant="danger">Flagged</Badge> : it.status === "estimated" ? <Badge variant="outline">Estimated</Badge> : it.status === "unverified" ? <Badge variant="outline" className="border-[#FFB800]/40 text-[#FFB800]" data-testid={`record-unverified-${it.id}`} title="Imported via the Staging & Review gate — pending verification">Unverified</Badge> : <Badge variant="success">Verified</Badge>}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-0.5 justify-end">
                    {it.superseded ? (
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => restore(it)} data-testid="restore-record-btn">Restore</Button>
                    ) : isEditing ? (
                      <>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => saveEdit(it)} data-testid="save-edit-btn"><Check className="h-3.5 w-3.5 text-[#00E65B]" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(null)}><X className="h-3.5 w-3.5 text-muted-foreground" /></Button>
                      </>
                    ) : (
                      <>
                        {it.kpi_evidence && <Button variant="ghost" size="icon" className="h-7 w-7" title="View linked scorecard KPI" onClick={() => navigate(`/esg-scorecard?kpi=${encodeURIComponent(it.activity_type)}`)} data-testid="view-kpi-btn"><ClipboardCheck className="h-3.5 w-3.5 text-accent" /></Button>}
                        {it.source === "data_points" && it.not_calculated && <Button variant="ghost" size="icon" className="h-7 w-7" title="Assign an emission factor to calculate this record" onClick={() => openAssign(it)} data-testid={`assign-factor-btn-${it.id}`}><Calculator className="h-3.5 w-3.5 text-accent" /></Button>}
                        {it.source === "data_points" && it.emission_basis === "spend_based" && <Button variant="ghost" size="icon" className="h-7 w-7" title="Upgrade this spend-based estimate to a primary/activity factor" onClick={() => openSwap(it)} data-testid="swap-factor-btn"><ArrowLeftRight className="h-3.5 w-3.5 text-[#FFB800]" /></Button>}
                        {it.source === "data_points" && <Button variant="ghost" size="icon" className="h-7 w-7" title="View data lineage & full audit trail for this record" onClick={() => setProvItem(it)} data-testid={`view-lineage-btn-${it.id}`}><FileSearch className="h-3.5 w-3.5 text-accent" /></Button>}
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(it)} data-testid="edit-record-btn"><Pencil className="h-3.5 w-3.5 text-muted-foreground" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(it)} data-testid="delete-record-btn"><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Re-categorise dialog */}
      <Dialog open={recatOpen} onOpenChange={setRecatOpen}>
        <DialogContent className="max-w-md" data-testid="recat-dialog">
          <DialogHeader><DialogTitle className="font-display">Re-categorise {selectedRefs.length} record(s)</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Move the selected records to a different category / activity type from your master taxonomy.</p>
          <div><div className="label-eyebrow text-[10px] mb-1">Category</div>
            <Select value={recat.catId} onValueChange={(v) => setRecat({ catId: v, activity: "" })}>
              <SelectTrigger data-testid="recat-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent className="max-h-64">{allCats.map((c) => <SelectItem key={c.id} value={c.id}>{c.pillar} · {c.category}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {recatCat && (
            <div><div className="label-eyebrow text-[10px] mb-1">Activity type (optional)</div>
              <Select value={recat.activity} onValueChange={(v) => setRecat((r) => ({ ...r, activity: v }))}>
                <SelectTrigger data-testid="recat-activity"><SelectValue placeholder="Keep existing" /></SelectTrigger>
                <SelectContent>{(recatCat.activity_types || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => setRecatOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={applyRecat} data-testid="recat-apply-btn">Apply</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AiImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={load} />

      <Dialog open={!!replaceInfo} onOpenChange={(o) => !o && setReplaceInfo(null)}>
        <DialogContent className="max-w-md" data-testid="replace-history-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ArrowLeftRight className="h-4 w-4 text-accent" /> Bill replacement</DialogTitle></DialogHeader>
          {replaceInfo && (
            <div className="space-y-3 text-sm">
              <p className="text-xs text-muted-foreground">This record ({replaceInfo.activity_type} · {replaceInfo.period}) superseded {replaceInfo.supersedes_snapshot.length} prior bill record(s). The old record(s) stay in the Archived view and are excluded from every total.</p>
              {replaceInfo.supersedes_snapshot.map((s, i) => (
                <div key={i} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 bg-surface rounded-sm p-3" data-testid={`replace-history-row-${i}`}>
                  <div className="text-right">
                    <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Superseded (old)</div>
                    <div className="font-mono" data-testid={`replace-old-value-${i}`}>{formatNum(s.value)} {s.unit}</div>
                    <div className="font-mono text-xs text-muted-foreground">{s.currency_amount != null ? `${s.currency || ""} ${formatNum(s.currency_amount)}` : "no cost"}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-accent shrink-0" />
                  <div>
                    <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Current (new)</div>
                    <div className="font-mono" data-testid={`replace-new-value-${i}`}>{formatNum(replaceInfo.value)} {replaceInfo.unit}</div>
                    <div className="font-mono text-xs text-muted-foreground">{replaceInfo.currency_amount != null ? `${replaceInfo.currency || ""} ${formatNum(replaceInfo.currency_amount)}` : "no cost"}</div>
                  </div>
                </div>
              ))}
              <p className="text-[10px] text-muted-foreground">Toggle <b>Archived</b> in the toolbar to see the superseded record and restore it if this was a mistake.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign facility dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-sm" data-testid="assign-facility-dialog">
          <DialogHeader><DialogTitle className="font-display">Assign facility to {selectedRefs.length} record(s)</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Pick a site from your facility master list (managed in Settings → Facilities).</p>
          <Select value={assignFac} onValueChange={setAssignFac}>
            <SelectTrigger data-testid="assign-facility-select"><SelectValue placeholder="Select facility" /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="__none__">Unassigned (clear)</SelectItem>
              {facilities.map((f) => <SelectItem key={f.id} value={f.name}>{f.name}{f.location ? ` · ${f.location}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          {facilities.length === 0 && <p className="text-[10px] text-[#FFB800]">No facilities defined yet — add them in Settings → Facilities first.</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button variant="primary" disabled={!assignFac} onClick={applyAssignFacility} data-testid="assign-facility-apply-btn">Apply</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export reconciliation dialog */}
      <Dialog open={reconOpen} onOpenChange={setReconOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="recon-report-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><GitCompare className="h-4 w-4 text-accent" /> Export Reconciliation Report</DialogTitle></DialogHeader>
          {reconOpen && <ExportReconciliation />}
        </DialogContent>
      </Dialog>

      {/* Spend-factor swap dialog */}
      <Dialog open={!!swapItem} onOpenChange={(o) => { if (!o) setSwapItem(null); }}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="swap-factor-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ArrowLeftRight className="h-4 w-4 text-[#FFB800]" /> Upgrade to a primary factor</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Replace this spend-based estimate with a real activity factor and the physical quantity that just arrived. The prior estimate stays in the record's lineage.</p>

          {swapCur && (
            <div className="card-flat p-3 bg-surface/60" data-testid="swap-current">
              <div className="label-eyebrow text-[10px] mb-1">Current spend-based estimate</div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                <span>{swapCur.activity_type}</span>
                {swapCur.amount_reporting != null && <span className="font-mono text-muted-foreground">{formatNum(swapCur.amount_reporting)} {swapCur.currency_reporting}</span>}
                <Badge variant="warning" className="text-[9px]">Tier {swapCur.data_quality_tier || "D"} · {swapCur.factor_code}</Badge>
                <span className="font-mono font-semibold">{formatNum(swapCur.co2e_tonnes, { decimals: 3 })} tCO₂e</span>
              </div>
            </div>
          )}

          <div>
            <div className="label-eyebrow text-[10px] mb-1">Primary / activity factor</div>
            <Input value={swapQ} onChange={(e) => searchSwap(e.target.value)} placeholder="Search the factor library…" className="h-8 text-xs mb-2" data-testid="swap-factor-search" />
            <div className="max-h-52 overflow-y-auto space-y-1 pr-1" data-testid="swap-candidate-list">
              {swapCands.length === 0 && <div className="text-xs text-muted-foreground py-3 text-center">No matching factors.</div>}
              {swapCands.map((f) => (
                <button key={f.code} onClick={() => chooseFactor(f)} data-testid={`swap-candidate-${f.code}`}
                  className={`w-full text-left rounded-sm border px-2.5 py-1.5 transition-colors ${swapForm.factor_code === f.code ? "border-accent bg-accent/10" : "border-border hover:bg-surface-hover"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium truncate">{f.name}</span>
                    {f.same_scope && <Badge variant="outline" className="text-[8px] shrink-0">Scope {f.scope}</Badge>}
                  </div>
                  <div className="text-[10px] text-muted-foreground font-mono">{f.code} · {f.factor_kgco2e} kgCO₂e/{f.unit} · {f.registry}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><div className="label-eyebrow text-[10px] mb-1">Physical quantity</div>
              <Input type="number" step="any" value={swapForm.value} onChange={(e) => setSwapForm((s) => ({ ...s, value: e.target.value }))} placeholder="e.g. 50" className="h-8 text-xs" data-testid="swap-value" /></div>
            <div><div className="label-eyebrow text-[10px] mb-1">Unit</div>
              <Input value={swapForm.unit} onChange={(e) => setSwapForm((s) => ({ ...s, unit: e.target.value }))} placeholder="e.g. kg" className="h-8 text-xs" data-testid="swap-unit" /></div>
          </div>

          {(() => {
            const sf = swapCands.find((c) => c.code === swapForm.factor_code);
            const preview = sf && swapForm.value ? (Number(swapForm.value) * sf.factor_kgco2e / 1000) : null;
            if (preview == null) return null;
            return (
              <div className="card-flat p-3 flex items-center justify-between" data-testid="swap-preview">
                <div className="text-xs text-muted-foreground">New calculated emissions</div>
                <div className="text-right">
                  <div className="font-mono text-lg font-bold text-[#00E65B]">{formatNum(preview, { decimals: 3 })} tCO₂e</div>
                  {swapCur && <div className="text-[10px] text-muted-foreground">was {formatNum(swapCur.co2e_tonnes, { decimals: 3 })} (spend-based)</div>}
                </div>
              </div>
            );
          })()}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => setSwapItem(null)}>Cancel</Button>
            <Button variant="primary" onClick={applySwap} disabled={swapBusy || !swapForm.factor_code} data-testid="swap-apply-btn">{swapBusy ? "Upgrading…" : "Confirm upgrade"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign factor dialog (uncalculated records) */}
      <Dialog open={!!assignItem} onOpenChange={(o) => { if (!o) setAssignItem(null); }}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="assign-factor-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Calculator className="h-4 w-4 text-accent" /> Assign an emission factor</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Pick a factor to calculate {assignItem?.bulk ? `${assignItem.count} selected record(s)` : "this imported record"}. {assignItem?.is_monetary ? "Spend-based (EEIO) factors apply to the FX-converted amount." : "Activity factors apply to the physical quantity; spend factors apply to the converted amount."}</p>
          {assignItem && !assignItem.bulk && (
            <div className="card-flat p-3 bg-surface/60" data-testid="assign-current">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                <span className="font-medium">{assignItem.activity_type}</span>
                <span className="text-muted-foreground">{assignItem.category}</span>
                {assignItem.is_monetary && assignItem.amount_reporting != null && <span className="font-mono text-muted-foreground">{formatNum(assignItem.amount_reporting)} {assignItem.currency_reporting || "SGD"}</span>}
                {!assignItem.is_monetary && <span className="font-mono text-muted-foreground">{formatNum(assignItem.value)} {assignItem.unit || ""}</span>}
              </div>
            </div>
          )}
          <div>
            <Input value={assignQ} onChange={(e) => searchAssign(e.target.value)} placeholder="Search the factor library…" className="h-8 text-xs mb-2" data-testid="assign-factor-search" />
            <div className="max-h-56 overflow-y-auto space-y-1 pr-1" data-testid="assign-candidate-list">
              {assignCands.length === 0 && <div className="text-xs text-muted-foreground py-3 text-center">No matching factors.</div>}
              {assignCands.map((f) => (
                <button key={f.code} onClick={() => setAssignFactorCode(f.code)} data-testid={`assign-candidate-${f.code}`}
                  className={`w-full text-left rounded-sm border px-2.5 py-1.5 transition-colors ${assignFactorCode === f.code ? "border-accent bg-accent/10" : "border-border hover:bg-surface-hover"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium truncate">{f.name || f.code}</span>
                    {f.basis === "spend" && <Badge variant="warning" className="text-[8px] shrink-0">Spend · {f.data_quality_tier || "D"}</Badge>}
                  </div>
                  <div className="text-[10px] text-muted-foreground font-mono">{f.code} · {f.factor_kgco2e} kgCO₂e/{f.unit}{f.registry ? ` · ${f.registry}` : ""}</div>
                </button>
              ))}
            </div>
          </div>
          {(() => {
            const sf = assignCands.find((c) => c.code === assignFactorCode);
            if (!sf || assignItem?.bulk) return null;
            const amt = sf.basis === "spend" ? (assignItem?.amount_reporting != null ? Number(assignItem.amount_reporting) : Number(assignItem?.value || 0)) : Number(assignItem?.value || 0);
            const preview = Number.isFinite(amt) ? amt * sf.factor_kgco2e / 1000 : null;
            if (preview == null) return null;
            return (
              <div className="card-flat p-3 flex items-center justify-between" data-testid="assign-preview">
                <div className="text-xs text-muted-foreground">Calculated emissions</div>
                <div className="font-mono text-lg font-bold text-[#00E65B]">{formatNum(preview, { decimals: 3 })} tCO₂e</div>
              </div>
            );
          })()}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => setAssignItem(null)}>Cancel</Button>
            <Button variant="primary" onClick={applyAssign} disabled={assignBusy || !assignFactorCode} data-testid="assign-apply-btn">{assignBusy ? "Calculating…" : "Assign & calculate"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Verification audit log dialog */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="verification-log-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ScrollText className="h-4 w-4 text-accent" /> Verification audit log</DialogTitle></DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">Who verified imported records and when. Each record also keeps its own verified-by stamp.</p>
            <Button variant="outline" size="sm" onClick={downloadAuditCsv} data-testid="audit-csv-btn"><Download className="h-3.5 w-3.5" /> Download CSV</Button>
          </div>
          <div className="space-y-1.5">
            {logEvents.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center" data-testid="verification-log-empty">No verification activity yet.</div>}
            {logEvents.map((e) => (
              <div key={e.id} className="card-flat p-2.5 flex items-center justify-between gap-3" data-testid="verification-log-row">
                <div className="min-w-0">
                  <div className="text-xs font-medium truncate">
                    <Badge variant={e.action === "verify" ? "success" : "warning"} className="text-[9px] mr-1.5">{e.action === "verify" ? "Verified" : "Un-verified"}</Badge>
                    {e.count} record{e.count === 1 ? "" : "s"}{e.scope === "import_run" ? ` · import ${e.filename || "run"}` : ""}
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate">{e.actor}</div>
                </div>
                <div className="text-[10px] text-muted-foreground whitespace-nowrap font-mono">{e.at ? new Date(e.at).toLocaleString() : ""}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Immutable change-history (Data Lineage) dialog — RD-17 */}
      <Dialog open={changeLogOpen} onOpenChange={setChangeLogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="change-history-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><History className="h-4 w-4 text-accent" /> Change history — Data Lineage</DialogTitle></DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <p className="text-xs text-muted-foreground">Every create, edit, facility reassignment, emission-factor change, period edit and verification — captured with the user, timestamp and old → new value. Immutable: entries can never be edited or deleted.</p>
            <Button variant="outline" size="sm" className="shrink-0" onClick={downloadChangeHistoryCsv} disabled={changeLog.total === 0} data-testid="change-history-csv-btn"><Download className="h-3.5 w-3.5" /> Export CSV</Button>
          </div>
          <div className="space-y-1.5">
            {changeLog.entries.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center" data-testid="change-history-empty">No changes recorded yet.</div>}
            {changeLog.entries.map((e) => (
              <div key={e.id} className="card-flat p-2.5" data-testid="change-history-row">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <Badge variant={e.action === "delete" ? "danger" : e.action === "create" ? "success" : "outline"} className="text-[9px]">{AUDIT_ACTION_LABEL[e.action] || e.action}</Badge>
                    <span className="text-xs font-medium truncate">{e.label || e.record_id}</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap font-mono">{e.at ? new Date(e.at).toLocaleString() : ""}</span>
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{e.actor}{e.role ? ` (${e.role})` : ""}</div>
                {(e.changes || []).map((c, i) => (
                  <div key={i} className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <span className="text-foreground/80">{c.field}</span>
                    <span className="font-mono line-through opacity-70">{c.old === null || c.old === undefined || c.old === "" ? "—" : String(c.old)}</span>
                    <ArrowRight className="h-2.5 w-2.5" />
                    <span className="font-mono text-foreground">{c.new === null || c.new === undefined || c.new === "" ? "—" : String(c.new)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          {changeLog.pages > 1 && (
            <div className="flex items-center justify-between pt-1" data-testid="change-history-pager">
              <span className="text-[11px] text-muted-foreground">Page {changeLog.page} of {changeLog.pages} · {changeLog.total} entries</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={changeLog.page <= 1} onClick={() => loadChangeLog(changeLog.page - 1)} data-testid="change-history-prev">Previous</Button>
                <Button variant="outline" size="sm" disabled={changeLog.page >= changeLog.pages} onClick={() => loadChangeLog(changeLog.page + 1)} data-testid="change-history-next">Next</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Per-record lineage + immutable audit trail (RD-17) */}
      <ProvenanceDialog pointId={provItem?.id} open={!!provItem} onClose={() => setProvItem(null)} />
    </div>
  );
}
